import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  GTT_TARGET_PRICE,
  GTT_TARGET_QUANTITY,
  GTT_INTRO_SEEN,
  TRIGGER_ORDER_WIZARD,
  WIZARD_DATA,
} from '../../constants/sessionStorage';
import StepByStep from '../StepByStep';
import BasicModal from '../BasicModal/BasicModal';
import { ModalCloseIcon, wizardHeaderCss, wizardTitleCss } from './Common';
import config from '../../config';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { deleteWizardKey } from '../../util/helpers/wizardHelpers';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import { selectPortfolioPendingTransactions } from '../../store/selectors/portfolioTransactionsSelector';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { PORTFOLIO_FINANCIAL_PENDING_TRANSACTIONS_LOADING } from '../../store/actions/portfolio/portfolioActionConstants';
import { portfolioFinancialPendingTransactions } from '../../store/actions/portfolio/portfolioActions';
import { getTriggerOrderDetails } from '../../store/actions/triggerOrders/triggerOrdersActions';
import { mergeTriggerOrderDetails } from '../../util/helpers/transcation/singleTransactionHelper';
import { FINANCIAL_TRANSACTIONS } from '../../constants/accountTransactionConstants';
import TriggerOrderDetailsModal from '../Modals/TriggerOrderDetails/TriggerOrderDetailsModal';
import TargetPriceProductSelection from './TargetPriceProductSelection';
import TargetPriceOrderEntry from './TargetPriceOrderEntry';
import TargetPriceOrderReview from './TargetPriceOrderReview';
import TargetPriceOrderConfirmation from './TargetPriceOrderConfirmation';
import TargetPriceInfoDrawer from './TargetPriceInfoDrawer';

// Buy-at-Target-Price (GTT / trigger-order) modal flow. Mirrors PriceAlertFlow:
// a BasicModal shell hosting a StepByStep wizard, all four screens
// (TargetPriceProductSelection, TargetPriceOrderEntry, TargetPriceOrderReview,
// TargetPriceOrderConfirmation) implemented.
const GTTFlow = ({ isModalOpen, handleCloseModal, onSwitchToBuyFlow }) => {
  const dispatch = useDispatch();
  const { brokerTheme } = config;
  const isGS = brokerTheme === 'GoldSilver';
  const accountUid = useSelector(getCurrentAccountUid);
  const pendingTransactions = useSelector(selectPortfolioPendingTransactions);
  const isPendingTransactionsLoading = useSelector(
    selectIsLoadingByActionType(
      PORTFOLIO_FINANCIAL_PENDING_TRANSACTIONS_LOADING,
    ),
  );

  const [wizardData, setWizardData] = useState(
    () => retrieveFromSessionStorage(WIZARD_DATA) || {},
  );
  const [wizardTitle, setWizardTitle] = useState('');
  const [goBackHandler, setGoBackHandler] = useState(null);
  const [closeHandler, setCloseHandler] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isViewOrderRequested, setIsViewOrderRequested] = useState(false);
  // The placed order's details from the single transaction API, the same call
  // (and so the same rows) the Financial Balances table makes for an Active
  // order. null until that call returns, and if it fails: the details modal
  // then opens with the order's row alone.
  const [orderDetails, setOrderDetails] = useState(null);
  // Whether that call is still in flight. The details modal opens once it has
  // finished (see isOrderDetailsShown), and until then the confirmation screen
  // spins.
  const [isOrderDetailsPending, setIsOrderDetailsPending] = useState(false);
  // The details call can land after the flow was left without the user
  // pressing anything here (a route change, such as the browser's Back, closes
  // it), when there is nothing left to update.
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  // Auto-open the "How Buy at Target Price Works" intro once per flow session,
  // whenever the flow opens — regardless of the entry surface (Header link,
  // Buy page, product card) or which step it lands on. Gated by GTT_INTRO_SEEN,
  // which closeModal clears so a freshly reopened flow shows it again.
  useEffect(() => {
    if (isModalOpen && !retrieveFromSessionStorage(GTT_INTRO_SEEN)) {
      setIsInfoOpen(true);
    }
  }, [isModalOpen]);

  const handleInfoClose = () => {
    storeInSessionStorage(GTT_INTRO_SEEN, true);
    setIsInfoOpen(false);
  };

  // BasicModal only locks <body>, which doesn't reliably stop the viewport
  // scroll on mobile (the page behind kept its own scrollbar). The viewport
  // scrollbar derives from the root <html> element, so lock overflow there
  // while the modal is open and restore the previous value on close.
  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }
    const { style } = document.documentElement;
    const previousOverflow = style.overflow;
    style.overflow = 'hidden';
    return () => {
      style.overflow = previousOverflow;
    };
  }, [isModalOpen]);

  const TriggerOrderSteps = [
    { children: TargetPriceProductSelection },
    { children: TargetPriceOrderEntry },
    { children: TargetPriceOrderReview },
    { children: TargetPriceOrderConfirmation },
  ];

  const setDataForWizard = (value) => {
    setWizardData((prev) => {
      const next = { ...prev, ...value };
      storeInSessionStorage(WIZARD_DATA, next);
      return next;
    });
  };

  const closeModal = () => {
    setIsViewOrderRequested(false);
    setOrderDetails(null);
    setIsOrderDetailsPending(false);
    handleCloseModal();
    removeFromSessionStorage(WIZARD_DATA);
    removeFromSessionStorage(GTT_TARGET_PRICE);
    removeFromSessionStorage(GTT_TARGET_QUANTITY);
    removeFromSessionStorage(GTT_INTRO_SEEN);
    // StepByStep persists the current step inside the nested WIZARD object, so
    // clear it there (removeFromSessionStorage only touches top-level keys) to
    // avoid leaking the step into the next time the flow opens.
    removeFromSessionStorage(TRIGGER_ORDER_WIZARD);
    deleteWizardKey(TRIGGER_ORDER_WIZARD);
  };

  // The order Review just placed (it records the UID in wizardData), as a row
  // of the transaction-requests list it reloads — the shape the details modal
  // reads.
  const placedOrder = Array.isArray(pendingTransactions)
    ? pendingTransactions.find(
        (transaction) =>
          !!wizardData?.triggerOrderUid &&
          transaction.TriggerOrderUid === wizardData.triggerOrderUid,
      )
    : undefined;
  // The details modal opens once everything it shows is there: the order's row,
  // and its details (or the details call has failed, when it opens with the row
  // alone). This modal stays up until then, so a slow reload can't leave the
  // user with neither modal showing.
  const isOrderDetailsShown =
    isViewOrderRequested && !!placedOrder && !isOrderDetailsPending;
  // The row merged with the details the same way the Financial Balances table
  // does (the row's own fields win).
  const orderForDetails =
    placedOrder && mergeTriggerOrderDetails(orderDetails, placedOrder);
  // Spinner on the confirmation screen from View Order until the details modal
  // can open: while the list the row comes from loads, and while the details
  // are fetched.
  const isViewOrderLoading =
    isViewOrderRequested &&
    !isOrderDetailsShown &&
    (isPendingTransactionsLoading || isOrderDetailsPending);

  // "View Order" on the confirmation screen: swap this modal for the order's
  // details. GTTFlow stays mounted (hosts unmount it on handleCloseModal) so it
  // can host the details modal; closing that modal closes the flow for real.
  const handleViewOrder = () => {
    // Not in the list yet (reload still in flight, or the page was refreshed
    // on the confirmation step): fetch it again. The swap happens as soon as
    // the row arrives.
    if (!placedOrder) {
      dispatch(
        portfolioFinancialPendingTransactions({
          accountUid,
          content: FINANCIAL_TRANSACTIONS,
        }),
      );
    }
    // The order's details, through the same action the Financial Balances
    // table uses for an Active order (getTriggerOrderDetails, which keeps them
    // out of the store the transaction tables read). The details modal waits
    // for them (the confirmation screen spins meanwhile); if the call fails,
    // for a 500 too (it opts out of the redirect to the error page, which would
    // unmount this flow), the modal opens with the row alone instead. Unlike
    // the table, which only fetches for Active rows and opens the others as
    // they are, this fetches whatever the status: the order was just placed and
    // its row may not be in the list yet to read a status from, and an order
    // that has already moved on still has details worth showing.
    setOrderDetails(null);
    if (wizardData?.triggerOrderUid) {
      setIsOrderDetailsPending(true);
      dispatch(
        getTriggerOrderDetails({
          accountUid,
          triggerOrderUid: wizardData.triggerOrderUid,
          onSuccess: (details) => {
            if (isMounted.current) {
              setOrderDetails(details);
              setIsOrderDetailsPending(false);
            }
          },
          onError: () => {
            if (isMounted.current) {
              setIsOrderDetailsPending(false);
            }
          },
        }),
      );
    }
    setIsViewOrderRequested(true);
  };

  const handleBackButtonClick = () => {
    if (goBackHandler) {
      goBackHandler();
    }
  };

  const goBackFn = goBackHandler ? handleBackButtonClick : undefined;

  return (
    <>
      <BasicModal
        title={wizardTitle}
        size="lg"
        isOpen={isModalOpen && !isOrderDetailsShown}
        close={closeHandler || closeModal}
        closeIconVisible
        goBack={goBackFn}
        closeIcon={<ModalCloseIcon />}
        fixedHeight
        smallTitle
        headerCss={wizardHeaderCss}
        titleCss={wizardTitleCss}
        darkArrow
      >
        <StepByStep sessionScopeStep={TRIGGER_ORDER_WIZARD} fitParentHeight>
          {({
            goStepForward,
            goStepBack,
            skipPreviousStep,
            currentStepIndex,
          }) =>
            TriggerOrderSteps.map((step, index) =>
              React.createElement(step.children, {
                stepIndex: index,
                currentStepIndex,
                // StepByStep's own isLastStep is unreliable in function-as-child
                // mode (it reads the callback's arity, not the step count), so
                // compute it from the steps array. Screens use it to avoid
                // advancing past the last defined step while later screens are
                // still unimplemented.
                isLastStep: currentStepIndex >= TriggerOrderSteps.length - 1,
                handleBack: goStepBack,
                handleNext: goStepForward,
                skipPreviousStep,
                closeModal,
                wizardData,
                setWizardData: setDataForWizard,
                setWizardTitle,
                setGoBackHandler,
                setCloseHandler,
                isGS,
                onSwitchToBuyFlow,
                onViewOrder: handleViewOrder,
                isViewOrderLoading,
              }),
            )
          }
        </StepByStep>
        <TargetPriceInfoDrawer isOpen={isInfoOpen} onClose={handleInfoClose} />
      </BasicModal>
      <TriggerOrderDetailsModal
        isOpen={isOrderDetailsShown}
        onClose={closeModal}
        transaction={orderForDetails}
      />
    </>
  );
};

GTTFlow.propTypes = {
  isModalOpen: PropTypes.bool,
  handleCloseModal: PropTypes.func,
  onSwitchToBuyFlow: PropTypes.func,
};

export default GTTFlow;
